import { AudioDevicePreferences } from "@/enums/preferences";
import { useAuth } from "@/hooks/auth";

import type { AudioDeviceValue } from "@/models/preferences";

const getSavedDevice = ({
  type,
  preferences,
}: {
  type: "input" | "output";
  preferences: readonly { key: string; value: unknown }[];
}): AudioDeviceValue | null => {
  const key =
    type === "input"
      ? AudioDevicePreferences.input
      : AudioDevicePreferences.output;

  const pref = preferences.find((preference) => preference.key === key);
  if (!pref?.value || typeof pref.value !== "object") {
    return null;
  }

  const val = pref.value as AudioDeviceValue;
  return val.device_id ? val : null;
};

const findMatchingDevice = ({
  savedDevice,
  availableDevices,
}: {
  savedDevice: AudioDeviceValue;
  availableDevices: MediaDeviceInfo[];
}): string | null => {
  const byId = availableDevices.find(
    (d) => d.deviceId === savedDevice.device_id
  );
  if (byId) {
    return byId.deviceId;
  }

  // Fallback: match by label (handles device ID rotation on reinstall)
  const byLabel = availableDevices.find((d) => d.label === savedDevice.label);
  if (byLabel) {
    return byLabel.deviceId;
  }

  return null;
};

export const useAudioDevicePreferences = () => {
  const { currentUser } = useAuth();

  const restoreDevice = ({
    type,
    availableDevices,
  }: {
    type: "input" | "output";
    availableDevices: MediaDeviceInfo[];
  }): string | null => {
    if (!currentUser?.preferences) {
      return null;
    }

    const savedDevice = getSavedDevice({
      type,
      preferences: currentUser.preferences,
    });
    if (!savedDevice) {
      return null;
    }

    return findMatchingDevice({ savedDevice, availableDevices });
  };

  return {
    restoreDevice,
  };
};
